import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import fleetApi from "@/lib/fleetApi";
import { useAuth } from "@/context/AuthContext";
import { visibleNotifications } from "@/lib/notifications";
import { fmtDate } from "@/lib/time";
import { PageHeader, Panel, Loading, EmptyState } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { CheckCheck, AlertTriangle, Info, ShieldAlert } from "lucide-react";

const TIER = {
  urgent: { cls: "text-red-300 bg-red-500/10 border-red-500/30", icon: AlertTriangle },
  attention: { cls: "text-amber-300 bg-amber-500/10 border-amber-500/30", icon: ShieldAlert },
  info: { cls: "text-blue-300 bg-blue-500/10 border-blue-500/30", icon: Info },
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return fmtDate(d);
}

export default function NotificationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await fleetApi.get("/notifications");
      setItems(visibleNotifications(data, user));
    } catch (e) {
      setItems([]);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (n) => {
    if (n.status === "read") return;
    setItems((p) => p.map((x) => (x.id === n.id ? { ...x, status: "read" } : x)));
    try { await fleetApi.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
  };

  const markAll = async () => {
    const unread = (items || []).filter((n) => n.status === "unread");
    if (!unread.length) return;
    setItems((p) => p.map((x) => ({ ...x, status: "read" })));
    await Promise.allSettled(unread.map((n) => fleetApi.post(`/notifications/${n.id}/read`)));
    toast.success("All notifications marked read.");
  };

  if (items === null) return <Loading label="Loading notifications…" />;
  const unreadCount = items.filter((n) => n.status === "unread").length;

  return (
    <div data-testid="notifications-page">
      <PageHeader title="Notifications" subtitle={`Alerts for your role${unreadCount ? ` · ${unreadCount} unread` : ""}`}>
        <Button onClick={markAll} disabled={!unreadCount} data-testid="mark-all-read-btn"
          className="gap-2 bg-blue-700 text-white hover:bg-blue-800">
          <CheckCheck className="h-4 w-4" /> Mark all read
        </Button>
      </PageHeader>

      <Panel testid="notifications-panel">
        {items.length === 0 ? (
          <EmptyState label="You're all caught up. No notifications for your role." />
        ) : (
          <ul className="divide-y divide-slate-800">
            {items.map((n) => {
              const t = TIER[n.tier] || TIER.info;
              const Icon = t.icon;
              return (
                <li key={n.id} onClick={() => markRead(n)} data-testid={`notification-${n.id}`}
                  className={`flex cursor-pointer items-start gap-3 px-5 py-4 transition-colors hover:bg-slate-800/40 ${n.status === "unread" ? "bg-slate-800/20" : ""}`}>
                  <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${t.cls}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-100">{n.title}</span>
                      {n.status === "unread" && <span className="h-2 w-2 rounded-full bg-blue-500" data-testid="unread-dot" />}
                    </div>
                    {n.body && <p className="mt-0.5 text-sm text-slate-400">{n.body}</p>}
                    <p className="mt-1 text-xs text-slate-600">{timeAgo(n.created_at)}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      <p className="mt-4 text-xs text-slate-600" data-testid="driver-alerts-note">
        Driver-only alerts (like trip assignments) are delivered to drivers in the RideStamp mobile app, with sound, and are not shown here.
      </p>
    </div>
  );
}
